import { useAuth } from '@/_core/hooks/useAuth'
import { Header } from '@/components/Header'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Calendar, Mail, Globe, Award, Star, User } from 'lucide-react'
import { useLanguage } from '@/contexts/LanguageContext'

export default function Profile() {
  const { user, loading, logout } = useAuth()
  const { t } = useLanguage()

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Header />
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">Loading...</div>
        </div>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Header />
        <Card className="max-w-md mx-auto mt-8">
          <CardHeader>
            <CardTitle>{t.profile || 'Profile'}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">You need to be logged in to view your profile.</p>
            <Button asChild>
              <a href="/login">{t.login || 'Login'}</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <Header />
      <div className="max-w-4xl mx-auto mt-8">
        <Card>
          <CardHeader className="flex flex-row items-center gap-4">
            <Avatar className="h-24 w-24">
              <AvatarImage src={user.avatar} />
              <AvatarFallback>
                <User className="h-12 w-12" />
              </AvatarFallback>
            </Avatar>
            <div>
              <CardTitle className="text-3xl">{user.name || user.username}</CardTitle>
              <p className="text-muted-foreground flex items-center gap-2 mt-2">
                <Mail className="h-4 w-4" />
                {user.email || 'No email'}
              </p>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Award className="h-5 w-5" />
                  {t.header?.trustLevel || 'Account Information'}
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Username:</span>
                    <span className="font-medium">{user.username}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Login Method:</span>
                    <Badge variant="outline">{user.loginMethod}</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Trust Level:</span>
                    <Badge variant="secondary">Level {user.trustLevel || 0}</Badge>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Star className="h-5 w-5" />
                  {t.header?.points || 'Points & Stats'}
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Points:</span>
                    <span className="font-bold text-2xl">{user.points || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Last Login:</span>
                    <span className="font-medium">
                      {user.lastSignedIn ? new Date(user.lastSignedIn).toLocaleDateString() : 'Never'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-6 border-t">
              <h3 className="text-lg font-semibold mb-4">Actions</h3>
              <div className="flex gap-4">
                <Button onClick={logout}>{t.logout || 'Logout'}</Button>
                <Button variant="outline" asChild>
                  <a href="/monitor">View Domain Monitor</a>
                </Button>
                <Button variant="outline" asChild>
                  <a href="/generator">Domain Generator</a>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}